// One team's page: its numbers, its agents, a job form, what's waiting for
// you, everything it did (work log), the emails it's on and its latest report.
import { EmptyState } from '@/components/primitives/EmptyState'
import { ErrorState } from '@/components/primitives/ErrorState'
import {
  type ApprovalItem,
  type TeamAgent,
  type TeamWork,
  fetchApprovals,
  fetchTeam,
  fetchTeamWork,
  sendMessage
} from '@/lib/ai-team'
import { notifyError, queryClient } from '@/lib/query'
import { runRecordAction } from '@/lib/sheet'
import { cn } from '@/lib/utils'
import { AgentAvatar } from '@/pages/ai-team/AgentAvatar'
import { CHIP, HUBS, type Tone, card, hubAgents, money, totals } from '@/pages/ai-team/hubs'
import { timeAgo } from '@/pages/ai-team/workflows/shared'
import { useQuery } from '@tanstack/react-query'
import {
  AlertTriangle,
  BellRing,
  ChevronDown,
  DollarSign,
  FileText,
  Loader2,
  Mail,
  Send,
  Sparkles,
  Zap
} from 'lucide-react'
import { type FormEvent, useEffect, useState } from 'react'
import { Link, Navigate, useLocation, useParams } from 'react-router-dom'
import { toast } from 'sonner'
import { DecisionCard, Decisions, JobForm, Pager, Stat, TeamToggle, field, goBtn, sectionTitle, teamStatus } from './parts'
import { ResultList, useResults } from './ResultCard'
import { NewPostCard, RecentPosts } from './NewPostCard'

const RUN_TONE: Record<string, Tone> = {
  running: 'emerald',
  done: 'neutral',
  waiting: 'amber',
  failed: 'red',
  skipped: 'neutral',
  stopped: 'neutral'
}

/** Keyed by team, so switching teams starts fresh (results, job form, work log page). */
export function TeamPage() {
  const { hubId } = useParams()
  return <TeamPageInner key={hubId} hubId={hubId} />
}

function TeamPageInner({ hubId }: { hubId?: string }) {
  const hub = HUBS.find((h) => h.id === hubId)
  const team = useQuery({ queryKey: ['ai-team'], queryFn: fetchTeam, refetchInterval: 30_000 })
  const approvals = useQuery({ queryKey: ['ai-approvals'], queryFn: fetchApprovals, refetchInterval: 5_000 })
  const results = useResults(`team:${hubId}`)
  // Same key as the work log's first "Everything" page, so the two share one request.
  const work = useQuery({
    queryKey: ['ai-work', hub?.id, 1, 'all'],
    queryFn: () => fetchTeamWork(hub!.layers, 1, 'all'),
    enabled: !!hub,
    refetchInterval: 15_000
  })

  // "New post" on the Overview links here with #new-post.
  const { hash } = useLocation()
  const ready = !team.isLoading
  useEffect(() => {
    if (ready && hash) document.getElementById(hash.slice(1))?.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }, [ready, hash])

  if (!hub) return <Navigate to="/apps/ai-team" replace />
  if (team.isLoading) return <TeamSkeleton />
  if (team.error) return <ErrorState error={team.error} onRetry={() => team.refetch()} />

  const agents = hubAgents(hub, team.data?.agents ?? [])
  const keys = new Set(agents.map((a) => a.key))
  const waiting = (approvals.data?.items ?? []).filter((i) => keys.has(i.agent.key))
  const t = totals(agents)
  const status = teamStatus(agents)

  return (
    <div className="space-y-6">
      <section className="rounded-[36px] border border-neutral-200 bg-white bg-gradient-to-br from-emerald-500/10 via-transparent to-red-500/10 p-6 dark:border-neutral-800 dark:bg-black dark:from-emerald-500/15 dark:to-red-500/15">
        <div className="flex flex-wrap items-center gap-4">
          <span className={cn('flex size-14 shrink-0 items-center justify-center rounded-full', CHIP[hub.tone])}>
            <hub.icon className="size-6" />
          </span>
          <div className="min-w-0 flex-1">
            <h1 className="text-2xl font-bold text-neutral-900 dark:text-white">{hub.title}</h1>
            <p className="text-sm text-neutral-500 dark:text-neutral-400">{hub.subtitle}</p>
          </div>
          <span className={cn('rounded-full px-3 py-1 text-sm font-bold', CHIP[status.tone])}>{status.text}</span>
          <TeamToggle hub={hub} agents={agents} />
        </div>
      </section>

      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 xl:grid-cols-6">
        <Stat label="Needs you" value={waiting.length} icon={BellRing} tone={waiting.length ? 'amber' : 'emerald'} />
        <Stat label="Working now" value={t.working} icon={Zap} tone="emerald" />
        <Stat label="Runs today" value={t.runs} icon={Sparkles} tone={hub.tone} />
        <Stat label="Failed today" value={t.failed} icon={AlertTriangle} tone={t.failed ? 'red' : 'neutral'} />
        <Stat
          label={`Spent of ${money(t.budget)}`}
          value={t.spent}
          format={money}
          icon={DollarSign}
          tone="emerald"
          progress={t.budget ? t.spent / t.budget : 0}
        />
        <Stat label="Open emails" value={t.emails} icon={Mail} tone="violet" />
      </div>

      {hub.id === 'content' && (
        <div className="grid gap-6 xl:grid-cols-[minmax(0,1.2fr)_minmax(0,1fr)]">
          <NewPostCard />
          <RecentPosts />
        </div>
      )}


      <div className="grid gap-6 xl:grid-cols-[minmax(0,1fr)_minmax(0,1fr)]">
        <div className="min-w-0 space-y-6">
          <JobForm hub={hub} agents={agents} onStarted={results.add} />
          <ResultList items={results.items} onDismiss={results.remove} onSent={results.addSent} />
          <Decisions items={waiting} loading={approvals.isLoading} perPage={5} />
        </div>
        <section className="min-w-0 space-y-3">
          <h2 className={sectionTitle}>Agents in this team</h2>
          {agents.map((a) => (
            <AgentRow key={a.key} agent={a} />
          ))}
        </section>
      </div>

      <WorkLog layers={hub.layers} hubId={hub.id} approvals={approvals.data?.items ?? []} />


      <div className="grid gap-6 xl:grid-cols-2">
        <Emails work={work.data} loading={work.isLoading} />
        <Reports work={work.data} loading={work.isLoading} />
      </div>
    </div>
  )
}

function AgentRow({ agent: a }: { agent: TeamAgent }) {
  // Follows the saved value until the admin types; then holds what they typed.
  const [draft, setBudget] = useState<string | null>(null)
  const budget = draft ?? a.daily_budget_usd
  const amount = Number(budget)
  const valid = budget.trim() !== '' && Number.isFinite(amount) && amount >= 0
  const [saving, setSaving] = useState(false)
  const spent = Number(a.spent_today)
  const cap = Number(a.daily_budget_usd)
  const pct = cap ? Math.min(100, (spent / cap) * 100) : 0
  const state = !a.enabled ? 'idle' : a.working ? 'working' : a.waiting ? 'waiting' : a.failed_today ? 'error' : 'idle'
  const word = !a.enabled ? 'Off' : a.working ? 'Working' : a.waiting ? 'Needs you' : a.failed_today ? 'Had a problem' : 'Ready'
  const tone: Tone = !a.enabled ? 'neutral' : a.working ? 'emerald' : a.waiting ? 'amber' : a.failed_today ? 'red' : 'neutral'

  async function saveBudget(e: FormEvent) {
    e.preventDefault()
    if (!valid || saving) return
    setSaving(true)
    try {
      await runRecordAction('super_agents__agentprofile', a.id, 'set_budget', { daily_budget_usd: budget.trim() })
      toast.success(`${a.name}'s budget saved`)
      await queryClient.invalidateQueries({ queryKey: ['ai-team'] })
      setBudget(null)
    } catch (err) {
      notifyError('Could not save the budget', err)
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className={cn(card, 'space-y-3')}>
      <div className="flex items-start gap-3">
        <AgentAvatar agentKey={a.key} name={a.name} size="lg" state={state} />
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <p className="font-bold text-neutral-900 dark:text-white">{a.name}</p>
            <span className={cn('rounded-full px-2.5 py-0.5 text-xs font-bold', CHIP[tone])}>{word}</span>
          </div>
          <p className="flex items-center gap-1 truncate text-xs text-neutral-500 dark:text-neutral-400">
            <Mail className="size-3 shrink-0" /> {a.email}
          </p>
          <p className="mt-1.5 line-clamp-2 text-sm text-neutral-600 dark:text-neutral-300">{a.last_summary || a.duty}</p>
        </div>
      </div>
      <div className="flex flex-wrap gap-1.5 text-xs">
        <span className={cn('rounded-full px-2.5 py-0.5 font-semibold tabular-nums', CHIP.neutral)}>{a.runs_today} runs today</span>
        <span className={cn('rounded-full px-2.5 py-0.5 font-semibold tabular-nums', CHIP.violet)}>{a.open_threads} emails</span>
        {a.failed_today > 0 && (
          <span className={cn('rounded-full px-2.5 py-0.5 font-semibold tabular-nums', CHIP.red)}>{a.failed_today} failed</span>
        )}
        <span className="ml-auto text-neutral-500 dark:text-neutral-400">Last ran {timeAgo(a.last_run_at)}</span>
      </div>
      <form onSubmit={saveBudget} className="space-y-2">
        <div>
          <div className="flex justify-between text-xs text-neutral-500 dark:text-neutral-400">
            <span className="whitespace-nowrap">Spent today</span>
            <span className="whitespace-nowrap tabular-nums">
              {money(spent)} / {money(cap)}
            </span>
          </div>
          <div className="mt-1 h-1.5 overflow-hidden rounded-full bg-neutral-100 dark:bg-neutral-800">
            <div className={cn('h-full rounded-full', pct > 90 ? 'bg-red-500' : 'bg-emerald-500')} style={{ width: `${Math.max(2, pct)}%` }} />
          </div>
        </div>
        <div className="flex items-center justify-end gap-2">
        <label className="flex items-center gap-1.5 whitespace-nowrap text-xs font-semibold text-neutral-500 dark:text-neutral-400">
          Daily budget $
          <input
            type="number"
            min="0"
            step="0.01"
            value={budget}
            onChange={(e) => setBudget(e.target.value)}
            className="w-20 rounded-full border border-neutral-200 bg-white px-3 py-1.5 text-sm text-neutral-900 dark:border-neutral-800 dark:bg-neutral-900 dark:text-white"
          />
        </label>
        <button
          type="submit"
          disabled={saving || !valid || amount === cap}
          className="rounded-full border border-neutral-200 px-3 py-1.5 text-xs font-semibold text-neutral-900 transition-colors hover:bg-neutral-50 disabled:opacity-50 dark:border-neutral-800 dark:text-white dark:hover:bg-neutral-800"
        >
          {saving ? <Loader2 className="size-3.5 animate-spin" /> : 'Save'}
        </button>
        </div>
      </form>
    </div>
  )
}

function WorkLog({ layers, hubId, approvals }: { layers: string[]; hubId: string; approvals: ApprovalItem[] }) {
  const [filter, setFilter] = useState<'all' | 'failed' | 'waiting'>('all')
  const [page, setPage] = useState(1)
  const log = useQuery({
    queryKey: ['ai-work', hubId, page, filter],
    queryFn: () => fetchTeamWork(layers, page, filter),
    refetchInterval: 15_000,
    placeholderData: (prev) => prev
  })
  const work = log.data
  const loading = log.isLoading
  const error = log.error
  const onRetry = () => log.refetch()
  const byId = new Map(approvals.map((a) => [a.id, a]))
  const isWaiting = (r: TeamWork['runs'][number]) => r.status === 'waiting' || r.cards.length > 0
  const waitingCount = work?.waiting_total ?? 0
  const runs = work?.runs ?? []
  // The log shrank (or the filter changed) under us: don't sit past the last page.
  const lastPage = work?.pages
  useEffect(() => {
    if (lastPage !== undefined && page > Math.max(1, lastPage)) setPage(Math.max(1, lastPage))
  }, [lastPage, page])
  function pick(f: typeof filter) {
    setFilter(f)
    setPage(1)
  }
  return (
    <section className="space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 className={sectionTitle}>Work log</h2>
        <div className="flex flex-wrap items-center gap-1.5">
          {(['all', 'waiting', 'failed'] as const).map((f) => (
            <button
              key={f}
              type="button"
              onClick={() => pick(f)}
              aria-pressed={filter === f}
              className={cn(
                'rounded-full px-3.5 py-1.5 text-sm font-semibold transition-colors',
                filter === f ? CHIP.emerald : 'text-neutral-600 hover:bg-neutral-100 dark:text-neutral-300 dark:hover:bg-neutral-800'
              )}
            >
              {f === 'all' ? 'Everything' : f === 'waiting' ? `Waiting on you${waitingCount ? ` (${waitingCount})` : ''}` : 'Failed'}
            </button>
          ))}
          {work && work.total > 0 && (
            <span className="ml-1 flex items-center gap-2">
              <span className="text-xs tabular-nums text-neutral-500 dark:text-neutral-400">{work.total} total</span>
              <Pager page={page} pages={work.pages} onPage={setPage} />
            </span>
          )}
        </div>
      </div>
      {loading ? (
        <div className="h-48 animate-pulse rounded-[36px] bg-neutral-200 motion-reduce:animate-none dark:bg-neutral-800" />
      ) : error ? (
        <ErrorState error={error} onRetry={onRetry} />
      ) : runs.length === 0 ? (
        <EmptyState icon={Sparkles} title="Nothing here yet" description="Work this team does shows up here as it happens." />
      ) : (
        <ul className={cn(card, 'divide-y divide-neutral-100 p-2 dark:divide-neutral-800')}>
          {runs.map((r) => (
            <WorkRow key={r.id} run={r} item={byId} waiting={isWaiting(r)} />
          ))}
        </ul>
      )}
    </section>
  )
}

function Emails({ work, loading }: { work?: TeamWork; loading: boolean }) {
  const all = work?.threads ?? []
  const [page, setPage] = useState(1)
  const pages = Math.max(1, Math.ceil(all.length / 10))
  const threads = all.slice((Math.min(page, pages) - 1) * 10, Math.min(page, pages) * 10)
  return (
    <section className="space-y-3">
      <div className="flex items-center justify-between gap-3">
        <h2 className={sectionTitle}>Emails it's handling</h2>
        {pages > 1 && <Pager page={Math.min(page, pages)} pages={pages} onPage={setPage} />}
      </div>
      {loading ? (
        <div className="h-32 animate-pulse rounded-[36px] bg-neutral-200 motion-reduce:animate-none dark:bg-neutral-800" />
      ) : threads.length === 0 ? (
        <div className={card}>
          <p className="text-sm text-neutral-500 dark:text-neutral-400">No open emails.</p>
        </div>
      ) : (
        <ul className={cn(card, 'divide-y divide-neutral-100 p-2 dark:divide-neutral-800')}>
          {threads.map((t) => (
            <li key={t.id} className="flex items-center gap-3 px-3 py-2.5">
              <span className={cn('flex size-8 shrink-0 items-center justify-center rounded-full', CHIP.violet)}>
                <Mail className="size-4" />
              </span>
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-semibold text-neutral-900 dark:text-white">{t.subject || '(no subject)'}</p>
                <p className="truncate text-xs text-neutral-500 dark:text-neutral-400">
                  {t.contact} · {t.agent.name} · {timeAgo(t.updated_at)}
                </p>
              </div>
              <span className={cn('shrink-0 rounded-full px-2 py-0.5 text-xs font-bold', t.status === 'waiting' ? CHIP.amber : CHIP.neutral)}>
                {t.status_label}
              </span>
            </li>
          ))}
        </ul>
      )}
    </section>
  )
}

function Reports({ work, loading }: { work?: TeamWork; loading: boolean }) {
  const reports = work?.reports ?? []
  return (
    <section className="space-y-3">
      <h2 className={sectionTitle}>Latest reports</h2>
      {loading ? (
        <div className="h-32 animate-pulse rounded-[36px] bg-neutral-200 motion-reduce:animate-none dark:bg-neutral-800" />
      ) : reports.length === 0 ? (
        <div className={card}>
          <p className="text-sm text-neutral-500 dark:text-neutral-400">
            Reports appear after the first daily brief.
          </p>
        </div>
      ) : (
        reports.map((r) => (
          <article key={r.agent.key + r.date} className={cn(card, 'space-y-2')}>
            <p className="flex items-center gap-2 text-sm font-bold text-neutral-900 dark:text-white">
              <FileText className="size-4 text-emerald-500" /> {r.agent.name}
              <span className="text-xs font-normal text-neutral-500 dark:text-neutral-400">
                {r.period === 'weekly' ? 'Week of ' : ''}
                {new Date(r.date).toLocaleDateString()}
              </span>
            </p>
            <p className="whitespace-pre-wrap text-sm text-neutral-600 dark:text-neutral-300">{r.summary}</p>
          </article>
        ))
      )}
    </section>
  )
}

function TeamSkeleton() {
  const block = 'animate-pulse rounded-[36px] bg-neutral-200 motion-reduce:animate-none dark:bg-neutral-800'
  return (
    <div className="space-y-6">
      <div className={cn(block, 'h-28')} />
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 xl:grid-cols-6">
        {[0, 1, 2, 3, 4, 5].map((i) => (
          <div key={i} className={cn(block, 'h-[124px]')} />
        ))}
      </div>
      <div className="grid gap-6 xl:grid-cols-2">
        <div className={cn(block, 'h-64')} />
        <div className={cn(block, 'h-64')} />
      </div>
    </div>
  )
}


/** A run waiting with no decision card: answer the agent right here. */
function ReplyBox({ roomId, runId, agent }: { roomId: number; runId: number; agent: string }) {
  const [text, setText] = useState('')
  const [pending, setPending] = useState(false)
  async function send(e: FormEvent) {
    e.preventDefault()
    if (!text.trim() || pending) return
    setPending(true)
    try {
      await sendMessage(roomId, text.trim())
      setText('')
      toast.success(`Sent to ${agent}`)
      await queryClient.invalidateQueries({ queryKey: ['ai-work'] })
    } catch (err) {
      notifyError('Could not send your reply', err)
    } finally {
      setPending(false)
    }
  }
  return (
    <form onSubmit={send} className="flex gap-2">
      <label className="sr-only" htmlFor={`reply-${roomId}-${runId}`}>
        Reply to {agent}
      </label>
      <input
        id={`reply-${roomId}-${runId}`}
        value={text}
        onChange={(e) => setText(e.target.value)}
        placeholder={`Reply to ${agent}…`}
        className={field}
      />
      <button type="submit" disabled={pending || !text.trim()} className={cn(goBtn, 'shrink-0')}>
        {pending ? <Loader2 className="size-4 animate-spin" /> : <Send className="size-4" />}
        Send
      </button>
    </form>
  )
}

/** One piece of work; click to read all of it right here. */
function WorkRow({
  run: r,
  item: byId,
  waiting
}: {
  run: TeamWork['runs'][number]
  item: Map<number, ApprovalItem>
  waiting: boolean
}) {
  const [open, setOpen] = useState(waiting)
  // It started waiting on you since the page loaded: show what it needs.
  useEffect(() => {
    if (waiting) setOpen(true)
  }, [waiting])
  return (
    <li className="space-y-3 px-3 py-3">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        className="flex w-full items-start gap-3 rounded-[24px] text-left"
      >
        <AgentAvatar agentKey={r.agent.key} name={r.agent.name} size="sm" state={r.status === 'running' ? 'working' : 'idle'} />
        <span className="min-w-0 flex-1">
          <span className="flex flex-wrap items-center gap-2 text-sm font-semibold text-neutral-900 dark:text-white">
            {r.agent.name}
            <span className={cn('rounded-full px-2 py-0.5 text-xs font-bold', CHIP[RUN_TONE[r.status] ?? 'neutral'])}>
              {r.status === 'running' && (
                <span className="mr-1 inline-block size-1.5 rounded-full bg-emerald-500 align-middle motion-safe:animate-pulse" />
              )}
              {r.status_label}
            </span>
            <span className="text-xs font-normal text-neutral-500 dark:text-neutral-400">{r.trigger_label}</span>
          </span>
          <span className={cn('mt-0.5 block text-sm text-neutral-600 dark:text-neutral-300', !open && 'line-clamp-2')}>
            {r.summary || r.task || 'Working on it…'}
          </span>
          {r.error && <span className="mt-0.5 block text-xs text-red-600 dark:text-red-400">{r.error}</span>}
        </span>
        <span className="shrink-0 text-right text-xs text-neutral-500 dark:text-neutral-400">
          <span className="block">{timeAgo(r.started_at)}</span>
          <span className="block tabular-nums">${Number(r.cost).toFixed(2)}</span>
          <ChevronDown className={cn('ml-auto mt-1 size-4 transition-transform', open && 'rotate-180')} />
        </span>
      </button>
      {open && (
        <div className="space-y-3 pl-11">
          {r.task && r.summary && (
            <div className="rounded-[24px] bg-neutral-50 px-4 py-3 text-xs text-neutral-600 dark:bg-white/5 dark:text-neutral-300">
              <span className="font-semibold text-neutral-900 dark:text-white">What it was asked: </span>
              <span className="whitespace-pre-wrap">{r.task}</span>
            </div>
          )}
          {r.cards.map((id) => {
            const item = byId.get(id)
            return item ? (
              <DecisionCard key={id} entry={{ key: `c:${id}`, kind: 'one', item }} />
            ) : (
              <Link key={id} to="/apps/ai-team/inbox" className="text-sm font-semibold text-neutral-900 hover:underline dark:text-white">
                Decide in Needs you
              </Link>
            )
          })}
          {waiting && r.cards.length === 0 && r.room && <ReplyBox roomId={r.room} runId={r.id} agent={r.agent.name} />}
        </div>
      )}
    </li>
  )
}
